// CreateVariantModal.tsx
import React, { useState } from "react"
import { Modal, Typography } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import CreateVariantForm from "./CreateVariantForm.component"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons"
import { Project } from "stores/projectStore"
import { Variant, SelectedVariant } from "types/questionnaire.types"

/**
 * Props interface for the CreateVariantModal component
 */
interface CreateVariantModalProps {
  /** The project data */
  project: Project
  /** Function to insert variant text into the editor */
  insertVariantText: (text: string) => void
  /** The list of existing variants */
  variants: Variant[]
  /** Function to update the variants list */
  setVariants: (variants: Variant[]) => void
  /** The currently selected variant */
  selectedVariant: SelectedVariant
  /** Function to set the selected variant */
  setSelectedVariant: (variant: Variant | { name: string }) => void
  /** Function to refresh the project data */
  refresh: (id: string) => Promise<void>
  /** Function to save the questionnaire */
  saveQuestionnaire: (updatedVariants?: Variant[]) => Promise<void>
  /** Whether collaborative mode is enabled */
  collaborativeMode?: boolean
  /** Whether the button should be disabled */
  isDisabled?: boolean
}

/**
 * CreateVariantModal is a modal component for creating a new variant in a project.
 *
 * @component
 * @param props - The props for the component
 * @returns The rendered CreateVariantModal component
 */
const CreateVariantModal: React.FC<CreateVariantModalProps> = ({
  project,
  insertVariantText,
  variants,
  setVariants,
  selectedVariant,
  setSelectedVariant,
  refresh,
  saveQuestionnaire,
  collaborativeMode = false,
  isDisabled = false,
}) => {
  const { Title } = Typography
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)

  /**
   * Callback function to handle successful variant creation.
   *
   * @function createVariantCallback
   * @param updatedVariants - The updated variants array
   */
  const createVariantCallback = async (updatedVariants: Variant[]): Promise<void> => {
    notify.success(
      "Variant Created Successfully!",
      "The project has been created successfully."
    )
    await saveQuestionnaire(updatedVariants)
    refresh(project.id)
  }

  /**
   * Shows the modal for creating a new variant.
   * Displays an error notification if a non-master variant is selected.
   *
   * @function showModal
   */
  const showModal = (): void => {
    if (Object.keys(selectedVariant).length === 0) {
      setIsModalVisible(true)
    } else {
      notify.error(
        "Can Only Create Variants in Master!",
        "Please select Master variant to create a new variant."
      )
    }
  }

  return (
    <>
      <DataQButton onClick={showModal} type="secondary" className="text-black" isDisabled={isDisabled}>
        New Variant
        <FontAwesomeIcon icon={faPlusCircle} className="ml-2" />
      </DataQButton>
      <Modal
        open={isModalVisible}
        centered={true}
        footer={false}
        closable={false}
        destroyOnHidden={true}
      >
        <Title style={{ marginBottom: "0.5em" }} level={4}>
          Create New Variant
        </Title>
        <CreateVariantForm
          setVariants={setVariants}
          project={project}
          variants={variants}
          createVariantCallback={createVariantCallback}
          setIsModalVisible={setIsModalVisible}
          insertVariantText={insertVariantText}
          setSelectedVariant={setSelectedVariant}
          collaborativeMode={collaborativeMode}
        />
      </Modal>
    </>
  )
}

export default CreateVariantModal
